import { HtmlTreeNode, TreeNode } from './htmlTreeNode';
import { HtmlTree, IHtmlTreeDelegate } from './htmlTree';
import { Subject } from 'rxjs';
import { IFileInfo } from './fileData';
import { XsI18nPipe } from '@nx-fe/pipes';
import { DEVICE_OS_FAMILY, FeatureToggleService, GlobalEventBusService, IDeviceModel } from '@nx-fe/services';
import { ShortDateFormatPipe } from 'app/shared/pipes/time-format.pipe';
import { GlobalEvents } from 'app/core/global-event-constants';
import { OsTypes } from '../../remote-control-ng/definitions';
import { RcContext } from '../../remote-control-ng/context/rcContext';
import { RcFileSystemService } from '../../remote-control-ng/context/fileSystem';
import { OsDetectionService } from '../../remote-control-ng/services/osDetectionService';
import { PocketComm } from '../../remote-control-ng/messages';

export enum FileSearchState {
  Ready = 1,
  Searching,
  Cancelling,
  Done,
}

type IDosFileTreeResponse = PocketComm.IDosFileTreeResponse;

export class FileManagerState implements IHtmlTreeDelegate {
  public rootNode: HTMLElement;

  public fileTableRoot: HTMLElement;

  public breadCrumbNodes: TreeNode[] = [];
  public currentNode: TreeNode;
  public searchState: FileSearchState = FileSearchState.Ready;
  public searchButtonText: string;
  public fileTree: HtmlTree;
  public searchResultsVisible = false;
  public searchTree: HtmlTree;
  public searchPaths: string[];
  public searchPathIndex: number;
  public searchText = '';
  public searchRequestId = -1;
  public searchTextFocused = false;
  public selectedTreeNodes: TreeNode[] = [];

  protected initialized = false;

  private oldSearchCount = 0;
  private transferState: FileManagerState = null;
  private scrollPos = 0;
  private getFolderContentsSub: Subject<void> = new Subject<void>();
  private loadingDeviceContents = false;

  constructor(
    private rc: RcContext,
    private saI18nPipe: XsI18nPipe,
    private dateFormatPipe: ShortDateFormatPipe,
    private featureToggleService: FeatureToggleService,
    private globalEventBusService: GlobalEventBusService,
    private fileSystem: RcFileSystemService
  ) { }

  private get deviceOsType(): number {
    return RcContext.instance.deviceContext?.osType || OsTypes.Unknown;
  }

  /** Selectively clones this state for the purpose of transferring */
  public static cloneForTransfer = (
    oldState: FileManagerState,
    rcContext: RcContext,
    saI18nPipe: XsI18nPipe,
    dateFormatPipe: ShortDateFormatPipe,
    featureToggleService: FeatureToggleService,
    globalEventBusService: GlobalEventBusService,
    fileSystem: RcFileSystemService
  ): FileManagerState => {
    const newState = new FileManagerState(
      rcContext,
      saI18nPipe,
      dateFormatPipe,
      featureToggleService,
      globalEventBusService,
      fileSystem
    );

    // Don't need to clone breadcrumbs or DOM nodes since they are recomputed later in "exploreTo" and tree traversal
    newState.searchState = oldState.searchState;
    newState.searchButtonText = oldState.searchButtonText;
    newState.fileTree = HtmlTree.cloneForTransfer(
      oldState.fileTree,
      newState,
      saI18nPipe,
      dateFormatPipe,
      featureToggleService
    );
    newState.searchResultsVisible = oldState.searchResultsVisible;
    newState.searchTree = HtmlTree.cloneForTransfer(
      oldState.searchTree,
      newState,
      saI18nPipe,
      dateFormatPipe,
      featureToggleService
    );
    newState.searchText = oldState.searchText;
    newState.initialized = oldState.initialized;
    newState.scrollPos = oldState.scrollPos;
    newState.searchTextFocused = oldState.searchTextFocused;
    newState.selectedTreeNodes = oldState.selectedTreeNodes;

    return newState;
  };

  public init = () => {
    // The root table node for the entire file explorer tree
    this.rootNode = document.createElement('table');
    this.rootNode.className = 'tree-node';

    // Get the parent node from the template, to which the file explorer root node will be attached
    this.fileTableRoot = document.getElementById('filetable');
    if (this.fileTableRoot) {
      while (this.fileTableRoot.hasChildNodes()) {
        this.fileTableRoot.removeChild(this.fileTableRoot.lastChild);
      }
      this.fileTableRoot.appendChild(this.rootNode);
    }
    this.fileTree = new HtmlTree(this, false, this.saI18nPipe, this.dateFormatPipe, this.featureToggleService);
    this.searchTree = new HtmlTree(this, true, this.saI18nPipe, this.dateFormatPipe, this.featureToggleService);
    this.searchState = FileSearchState.Ready;
    this.updateSearchButtonText();
  };

  // This is a work around to let AngularJs know about changes within the
  private onUpdateCallback: Function;

  // This is a work around to let AngularJs know about changes within the
  public get onUpdate(): Function {
    return this.onUpdateCallback ? this.onUpdateCallback : () => {};
  }

  public set onUpdate(value: Function) {
    this.onUpdateCallback = value;
  }

  public onFocus = () => {
    this.searchTextFocused = true;
  };

  public onRemoteControlStarted = () => {
    // Clear loadingDeviceContents on reconnect to handle case when device was disconnected while fetching data.
    this.loadingDeviceContents = false;
  };

  public transfer = (oldState: FileManagerState) => {
    if (oldState && oldState.initialized) {
      // Need to clone the old state instead of simply reference it, since it may be used after its owner window
      // is destroyed (which can cause issues in IE and Edge)
      this.transferState = FileManagerState.cloneForTransfer(
        oldState,
        this.rc,
        this.saI18nPipe,
        this.dateFormatPipe,
        this.featureToggleService,
        this.globalEventBusService,
        this.fileSystem
      );

      this.scrollPos = this.transferState.getScrollValue();

      // Re-initialize if neccessary (in the case where we are transferring into an existing file explorer)
      if (this.initialized) {
        this.init();
      }
      this.transferStateContents();
      this.attachToPage();
      // HD-11638: this.initialize should be set to true when getting the device contents (getDeviceContents() method)
      // And also when transfering contents from another window (transfer() method)
      this.initialized = true;
    }
  };

  public transferStateContents = () => {
    if (this.fileTree && this.searchTree) {
      this.initFileTreeRoot();
      this.initSearchTreeRoot();

      if (this.fileTree.rootNode && this.searchTree.rootNode) {
        const htmlRootNode = new HtmlTreeNode(
          this.fileTree.rootNode,
          false,
          this,
          this.saI18nPipe,
          this.dateFormatPipe,
          this.featureToggleService
        );
        this.fileTree.htmlNodeMap.set(this.fileTree.rootNode.id, htmlRootNode);
        this.fileTree.rootNode.nodes = this.transferState.fileTree.rootNode.nodes;
        this.fileTree.transferViewRootNode = this.fileTree.rootNode;

        this.fileTree.transfer(
          this.transferState.fileTree.rootNode.nodes,
          this.fileTree.rootNode,
          this.transferState.fileTree.viewRootNode,
          htmlRootNode.childNodesTable
        );

        this.fileTree.rootNode.data = this.transferState.fileTree.rootNode.data;

        this.fileTree.viewRootNode = this.fileTree.rootNode;
        this.rootNode.appendChild(htmlRootNode.childNodes);
        this.exploreTo(this.fileTree.transferViewRootNode);

        this.searchTree.viewRootNode = this.searchTree.rootNode;
        this.searchTree.htmlNodeMap.clear();
        this.searchTree.currentId = 1;
        this.searchResultsVisible = this.transferState.searchResultsVisible;

        if (this.transferState.searchResultsVisible) {
          if (this.transferState.searchTree.isEmpty()) {
            const viewRootNode: TreeNode = this.fileTree.viewRootNode;
            const curHtmlNode = this.fileTree.htmlNodeMap.get(viewRootNode.id);

            if (this.rootNode.contains(curHtmlNode.childNodes)) {
              this.rootNode.removeChild(curHtmlNode.childNodes);
            }

            this.displayNoFilesFound();
          } else {
            this.searchResultsVisible = false;
            this.showSearchResults();
          }

          this.searchResultsVisible = true;
          const htmlSearchRootNode = new HtmlTreeNode(
            this.searchTree.rootNode,
            false,
            this,
            this.saI18nPipe,
            this.dateFormatPipe,
            this.featureToggleService
          );
          this.searchTree.htmlNodeMap.set(this.searchTree.rootNode.id, htmlSearchRootNode);

          this.searchTree.transfer(
            this.transferState.searchTree.rootNode.nodes,
            this.searchTree.rootNode,
            this.transferState.searchTree.viewRootNode,
            htmlSearchRootNode.childNodesTable
          );

          this.searchTree.viewRootNode = this.searchTree.rootNode;
          this.rootNode.appendChild(htmlSearchRootNode.childNodes);

          this.searchState = this.transferState.searchState;
          this.updateSearchButtonText();
        }

        this.searchText = this.transferState.searchText;

        this.searchTextFocused = this.transferState.searchTextFocused;
        let textSearchElement = null;
        if (this.searchTextFocused) {
          textSearchElement = document.getElementById('fileSearch');
        } else {
          textSearchElement = document.body;
        }

        if (textSearchElement) {
          textSearchElement.focus();
        }

        if (this.transferState.selectedTreeNodes && this.transferState.selectedTreeNodes.length > 0) {
          this.onChangeSelectedNode(this.findNode(this.transferState.selectedTreeNodes[0]));
        }

        // Transfer scroll position on the next digest (gives time for the view to update)
        setTimeout(() => {
          this.setTransferScrollPosition();
        });

        this.transferState = null;
      }
    }
  };

  public setTransferScrollPosition = () => {
    if (!this.scrollPos || !this.fileTableRoot) {
      return;
    }

    // Step must be done post link
    setTimeout(() => {
      this.fileTableRoot.scrollTop = this.scrollPos * this.fileTableRoot.scrollHeight;
    });
  };

  public canPopout = (): boolean =>
    this.searchState === FileSearchState.Done || this.searchState === FileSearchState.Ready;

  public attachToPage = () => {
    this.fileTableRoot = document.getElementById('filetable');
    if (this.fileTableRoot && this.rootNode && this.rootNode.parentElement !== this.fileTableRoot) {
      this.fileTableRoot.appendChild(this.rootNode);
    }
  };

  public getDeviceContents = (device?: IDeviceModel) => {
    if (this.loadingDeviceContents === true) {
      return;
    }
    this.loadingDeviceContents = true;
    this.initialized = true;
    if (this.fileTree.rootNode === undefined) {
      if (this.transferState != null) {
        this.transferStateContents();
        this.loadingDeviceContents = false;
      } else {
        this.initFileTreeRoot();
        const htmlRootNode = new HtmlTreeNode(
          this.fileTree.rootNode,
          false,
          this,
          this.saI18nPipe,
          this.dateFormatPipe,
          this.featureToggleService
        );
        this.fileTree.htmlNodeMap.set(this.fileTree.rootNode.id, htmlRootNode);
        this.fileSystem.queryDeviceDrives(
          data => {
            data.sort(this.sortFunction);
            this.populateRootNode(data, htmlRootNode);
            this.fileTree.rootNode.data = { fullPath: '', fileName: '', fileSize: '', formattedDate: '', date: null };

            if (device && this.isWindowsDevice(device)) {
              const drives = this.fileTree.rootNode.nodes;
              if (drives) {
                const cDrive = drives.find((node: TreeNode) => node.type == 'drive' && node.title == 'C');
                if (cDrive) {
                  this.clickNode(cDrive);
                }
              }
            }
            this.loadingDeviceContents = false;
          },
          () => {
            this.fileSystem.getFolderContents('/*', (data: IDosFileTreeResponse) => {
              const tree = data.tree;
              tree.sort(this.sortFunction);

              let needPopulateRootNode = false;

              if (this.fileTree.rootNode.nodes.length === 0) {
                // if rootNodes.nodes is empty then we should populate any data.
                needPopulateRootNode = true;
              } else {
                // otherwise we shouldn't populate duplicates (with different ids).
                //new items is missing old node.
                for (const dataItem of tree) {
                  if (this.fileTree.rootNode.nodes.filter(item => this.nodesAreEqual(item, dataItem)).length == 0) {
                    needPopulateRootNode = true;
                    break;
                  }
                }
                //new items has a new node.
                for (const dataItem of this.fileTree.rootNode.nodes) {
                  if (tree.filter(item => this.nodesAreEqual(item, dataItem)).length == 0) {
                    needPopulateRootNode = true;
                    break;
                  }
                }
              }

              if (needPopulateRootNode || this.fileTree.viewRootNode === undefined) {
                this.populateRootNode(tree, htmlRootNode);
              }

              this.fileTree.rootNode.data = {
                fullPath: '/',
                fileName: '',
                fileSize: '',
                formattedDate: '',
                date: null,
              };
              this.loadingDeviceContents = false;
            });
          }
        );
      }
    } else {
      const htmlRootNode = this.fileTree.htmlNodeMap.get(this.fileTree.rootNode.id);
      // Refresh root
      this.fileSystem.queryDeviceDrives(
        data => {
          data.sort(this.sortFunction);
          this.updateNodes(data, this.fileTree.rootNode, this.rootNode);
          this.loadingDeviceContents = false;
        },
        () => {
          htmlRootNode.addLoadingNode();
          this.fileSystem.getFolderContents('/*', (data: IDosFileTreeResponse) => {
            htmlRootNode.removeLoadingNode();

            // Append new folder content to the tree. Keep existing expanded Nodes
            data.tree.sort(this.sortFunction);
            this.updateNodes(data.tree, this.fileTree.rootNode, this.rootNode);
            this.loadingDeviceContents = false;
          });
        }
      );
    }
  };

  public showSearchResults = () => {
    if (!this.searchResultsVisible) {
      const viewRootNode: TreeNode = this.fileTree.viewRootNode;
      let curHtmlNode = this.fileTree.htmlNodeMap.get(viewRootNode.id);

      if (this.rootNode.contains(curHtmlNode.childNodes)) {
        this.rootNode.removeChild(curHtmlNode.childNodes);
      }

      const htmlRootNode = new HtmlTreeNode(
        this.searchTree.rootNode,
        false,
        this,
        this.saI18nPipe,
        this.dateFormatPipe,
        this.featureToggleService
      );
      this.searchTree.htmlNodeMap.set(this.searchTree.rootNode.id, htmlRootNode);

      curHtmlNode = this.searchTree.htmlNodeMap.get(this.searchTree.viewRootNode.id);
      const childNodes = curHtmlNode.childNodes;
      curHtmlNode.expandNode();
      this.rootNode.appendChild(childNodes);
      this.searchResultsVisible = true;
    }
  };

  public showFileTree = () => {
    if (this.searchResultsVisible) {
      const viewRootNode: TreeNode = this.searchTree.viewRootNode;
      let curHtmlNode = this.searchTree.htmlNodeMap.get(viewRootNode.id);

      if (this.rootNode.contains(curHtmlNode.childNodes)) {
        this.rootNode.removeChild(curHtmlNode.childNodes);
      }
      this.rootNode.innerHTML = '';

      if (!this.fileTree.htmlNodeMap.has(this.fileTree.rootNode.id)) {
        const htmlRootNode = new HtmlTreeNode(
          this.fileTree.rootNode,
          false,
          this,
          this.saI18nPipe,
          this.dateFormatPipe,
          this.featureToggleService
        );
        this.fileTree.htmlNodeMap.set(this.fileTree.rootNode.id, htmlRootNode);
      }

      curHtmlNode = this.fileTree.htmlNodeMap.get(this.fileTree.viewRootNode.id);
      const childNodes = curHtmlNode.childNodes;
      curHtmlNode.expandNode();
      this.rootNode.appendChild(childNodes);
      this.searchResultsVisible = false;
    }
  };

  public updateSearchButtonText = () => {
    switch (this.searchState) {
      case FileSearchState.Ready:
        this.searchButtonText = this.saI18nPipe.transform('fileExplorer_SearchButtonText_Search_Caption');
        break;
      case FileSearchState.Searching:
        this.searchButtonText = this.saI18nPipe.transform('xs_cancel_label');
        break;
      case FileSearchState.Cancelling:
        this.searchButtonText = this.saI18nPipe.transform('fileExplorer_SearchButtonText_Cancelling_Caption');
        break;
      case FileSearchState.Done:
        this.searchButtonText = this.saI18nPipe.transform('fileExplorer_SearchButtonText_Clear_Caption');
        break;
    }
  };

  public onSearchButton = () => {
    this.resetSelectedFiles();

    if (this.searchState === FileSearchState.Cancelling) {
      return;
    }

    if (
      this.searchState === FileSearchState.Ready &&
      this.searchRequestId === -1 &&
      this.searchText.trim().length > 2 &&
      /\S/.test(this.searchText)
    ) {
      this.searchPaths = [];
      const viewRootNode: TreeNode = this.fileTree.viewRootNode;
      for (const node of viewRootNode.nodes) {
        if (node.type === 'drive') {
          this.searchPaths.push(node.data.fullPath);
        }
      }
      if (this.searchPaths.length === 0) {
        if (viewRootNode.data) {
          this.searchPaths.push(viewRootNode.data.fullPath);
        } else {
          this.searchPaths.push('');
        }
      }

      this.initSearchTreeRoot();
      this.searchTree.viewRootNode = this.searchTree.rootNode;
      this.searchTree.htmlNodeMap.clear();
      this.searchTree.currentId = 1;
      this.showSearchResults();

      this.searchPathIndex = 0;
      this.searchState = FileSearchState.Searching;
      this.updateSearchButtonText();
      const osType = this.deviceOsType;
      if (
        !OsDetectionService.IsWindowsCe(osType) &&
        !OsDetectionService.IsWindows(osType) &&
        !OsDetectionService.IsLinux(osType) &&
        (this.rc.versionHi > 13 || (this.rc.versionHi === 13 && this.rc.versionLo >= 30))
      ) {
        // Agent 13.3 and up, non WinCE/Windows Mobile
        this.searchRequestId = this.fileSystem.searchFolderContents(
          this.searchText.trim(),
          this.searchPaths[this.searchPathIndex],
          this.updateSearchResults,
          () => {
            console.error('searchFolderContents returned error');
          }
        );
      } else {
        // Older and WinCE/Windows Mobile agents
        this.oldSearchCount = 0;
        this.searchRequestId = 0;
        this.oldStyleSearch(this.searchPaths[this.searchPathIndex], this.searchText.trim().toLowerCase());
      }
    } else if (this.searchState === FileSearchState.Searching) {
      this.fileSystem.cancelSearch(this.searchRequestId);

      this.searchRequestId = -1;
      this.searchReady();
    } else {
      this.searchReady();
    }
  };

  private updateSearchResults = (response: IDosFileTreeResponse) => {
    if (response.done) {
      if (this.searchState === FileSearchState.Cancelling) {
        this.setFileSearchState(FileSearchState.Done);
        this.updateSearchButtonText();
      } else {
        this.searchPathIndex++;
        // If there are more folders to search and the searchRequestId is still valid (not cancelled)
        if (this.searchPathIndex < this.searchPaths.length && this.searchRequestId !== -1) {
          this.searchTree.concatPopulateNodes(response.tree, this.searchTree.viewRootNode, this.rootNode);
        } else {
          this.searchRequestId = -1;
          if (this.searchState === FileSearchState.Searching) {
            this.setFileSearchState(FileSearchState.Done);
          }
          this.updateSearchButtonText();
          this.searchTree.concatPopulateNodes(response.tree, this.searchTree.viewRootNode, this.rootNode);
          if (this.searchTree.isEmpty()) {
            this.displayNoFilesFound();
          }
        }
      }
    } else if (this.searchRequestId !== -1) {
      this.searchTree.concatPopulateNodes(response.tree, this.searchTree.viewRootNode, this.rootNode);
    }
  };

  public searchButtonDisabled = (): boolean => this.searchState === FileSearchState.Cancelling;

  public hideSearchSpinner = (): boolean =>
    this.searchState !== FileSearchState.Cancelling && this.searchState !== FileSearchState.Searching;

  public download = (node: TreeNode) => {
    // trigger file download
    const fileInfo: IFileInfo = {
      fileName: node.data.fileName,
      filePath: node.data.fullPath,
    };
    this.globalEventBusService.broadcast(GlobalEvents.fileDownloadTriggered, fileInfo);
  };

  public stopDownload = () => {
    this.fileSystem.stopFileDownload();
  };

  public stopUpload = () => {
    this.fileSystem.stopFileUpload();
  };

  public stopCompress = (requestId: number) => {
    this.fileSystem.stopCompression(requestId);
  };

  public renameTo = (oldNode: TreeNode, newName: string) => {
    const oneTimeGetFolderContentsSubscription = this.getFolderContentsSub.subscribe(() => {
      this.resetSelectedFiles();

      const forSearchCloneNode = new TreeNode();
      forSearchCloneNode.parent = oldNode.parent;
      forSearchCloneNode.title = newName;
      this.onChangeSelectedNode(this.findNode(forSearchCloneNode));

      oneTimeGetFolderContentsSubscription.unsubscribe();
    });

    this.currentNode = oldNode.parent;
    const justPath = oldNode.data.fullPath.substring(0, oldNode.data.fullPath.indexOf(oldNode.data.fileName));
    const pathWithoutSlashInTheEnd = justPath.length > 1 ? justPath.substring(0, justPath.length - 1) : justPath;
    this.refreshExistingFileManagerFolder(pathWithoutSlashInTheEnd);
  };

  public deleteNode = (oldNode: TreeNode) => {
    const oneTimeGetFolderContentsSubscription = this.getFolderContentsSub.subscribe(() => {
      this.resetSelectedFiles();

      oneTimeGetFolderContentsSubscription.unsubscribe();
      this.onUpdate();
    });

    this.currentNode = oldNode.parent;
    const justPath = oldNode.data.fullPath.substring(0, oldNode.data.fullPath.lastIndexOf(oldNode.data.fileName));
    const pathWithoutSlashInTheEnd = justPath.length > 1 ? justPath.substring(0, justPath.length - 1) : justPath;
    this.refreshExistingFileManagerFolder(pathWithoutSlashInTheEnd);
  };

  public exploreTo = (node: TreeNode) => {
    if (node.type === 'folder' || node.type === 'drive') {
      this.toggle(node);
      let viewRootNode: TreeNode = this.fileTree.viewRootNode;
      let curHtmlNode = this.fileTree.htmlNodeMap.get(viewRootNode.id);
      curHtmlNode.tooltip.mouseOut();

      if (this.searchState !== FileSearchState.Ready) {
        this.searchReady();
      }

      if (this.rootNode.contains(curHtmlNode.childNodes)) {
        this.rootNode.removeChild(curHtmlNode.childNodes);
      }
      this.breadCrumbNodes = [];
      let curNode = node;
      while (curNode !== null) {
        this.breadCrumbNodes.unshift(curNode);
        curNode = curNode.parent;
      }

      if (viewRootNode.id !== 0) {
        const child = this.fileTree.htmlNodeMap.get(viewRootNode.id);
        child.expandNode();
        child.showChildNodes();
      }

      this.fileTree.viewRootNode = viewRootNode = node;

      curHtmlNode = this.fileTree.htmlNodeMap.get(viewRootNode.id);
      const childNodes = curHtmlNode.childNodes;
      curHtmlNode.expandNode();
      this.rootNode.appendChild(childNodes);

      this.onCheckFileSelection();
    }
  };

  public toggle = (node: TreeNode) => {
    if (node.type === 'folder' || node.type === 'drive') {
      // Trigger Refresh directory contents on every Expand node

      if (!node.collapsed) {
        const expandingHTMLNode: HtmlTreeNode = this.fileTree.htmlNodeMap.get(node.id);
        if (expandingHTMLNode.isLoading() || !node.data.fullPath) {
          return;
        }
        expandingHTMLNode.expandNode();
        expandingHTMLNode.addLoadingNode();
        this.fileSystem.getFolderContents(this.trimEnd(node.data.fullPath, '/') + '/*', (data: IDosFileTreeResponse) => {
          expandingHTMLNode.removeLoadingNode();

          // Append new folder content to the tree. Keep existing expanded Nodes

          data.tree.sort(this.sortFunction);
          this.updateNodes(data.tree, node, this.rootNode);

          this.getFolderContentsSub.next();
        });
      }

      this.onCheckFileSelection();
    }
  };

  // Clears all nodes in file manager
  public clearNodes = () => {
    // Remove from the HtmlTree all nodes which are not present in the source [deleted/renamed nodes]
    this.removeAllNodes();
    const node = this.fileTree.rootNode;
    if (node && node.nodes) {
      this.fileTree.htmlNodeMap.delete(node.id);
    }
    this.fileTree.rootNode = undefined;
  };

  // Removes all child nodes under root node.
  private removeAllNodes = () => {
    const node = this.fileTree.rootNode;
    if (node && node.nodes) {
      this.resetSelectedFiles();
      for (const n of node.nodes) {
        this.removeTreeNodes(n);
      }
    }
  };

  // Loads expanded nodes recursively within this tree node
  public updateNodes = (
    source: TreeNode[],
    node: TreeNode,
    element: HTMLElement,
    fullUpdateSubTree: boolean = false
  ) => {
    let newItems = [];
    const oldItems = [];

    if (Array.isArray(node.nodes)) {
      // Remove from the HtmlTree all nodes which are not present in the source [deleted/renamed nodes]
      for (const n of node.nodes) {
        const elementIndex = source.findIndex(e => e.title.toLowerCase() === n.title.toLowerCase());

        if (elementIndex < 0 || fullUpdateSubTree) {
          // Remove from HTMLElement tree-node child-nodes and from Dictionary
          this.removeTreeNodes(n);
        }
      }

      // Skip in the source all the nodes which are already build in the tree [existing nodes]
      // Add only new/renamed nodes

      for (const n of source) {
        const existingNode = node.nodes.find(e => e.title.toLowerCase() === n.title.toLowerCase());
        if (!existingNode || fullUpdateSubTree) {
          newItems.push(n);
        } else {
          oldItems.push(existingNode);
        }
      }
    } else {
      // There are only new nodes to be added
      newItems = source;
    }

    node.nodes = [];

    // Add New Nodes to the tree
    if (Array.isArray(newItems) && newItems.length > 0) {
      this.fileTree.populateNodes(newItems, node, element);
    }

    // Keep old Nodes as they have already generated ids
    if (Array.isArray(oldItems) && oldItems.length > 0) {
      node.nodes = node.nodes.concat(oldItems);

      // Loads recursively expanded child nodes
      oldItems
        .filter(nd => !nd.collapsed)
        .forEach(child => {
          this.fileSystem.getFolderContents(this.trimEnd(child.data.fullPath, '/') + '/*', (newData: IDosFileTreeResponse) => {
            newData.tree.sort(this.sortFunction);
            this.updateNodes(newData.tree, child, this.rootNode);
          });
        });
    }
  };

  private nodesAreEqual = (node1: TreeNode, node2: TreeNode) =>
    node1.title === node2.title &&
    node1.type === node2.type &&
    node1.data.fileName === node2.data.fileName &&
    node1.data.fileSize === node2.data.fileSize &&
    node1.data.formattedDate === node2.data.formattedDate &&
    node1.data.fullPath === node2.data.fullPath;

  // Recursively Remove all the child elements of a DOM node and from Dictionary
  private removeTreeNodes = (node: TreeNode) => {
    if (Array.isArray(node.nodes)) {
      node.nodes.forEach(child => {
        this.removeTreeNodes(child);
      });
    }
    this.fileTree.htmlNodeMap
      .get(node.parent.id)
      .childNodesTable.removeChild(this.fileTree.htmlNodeMap.get(node.id).rowNode);
    this.fileTree.htmlNodeMap.delete(node.id);
  };

  public onScroll = () => {
    const tableheader = document.getElementById('tableheader');
    const filetable = document.getElementById('filetable');

    tableheader.style.marginLeft = -filetable.scrollLeft + 'px';
    this.updateScrollPos();
  };

  public updateScrollPos = () => {
    if (this.fileTableRoot) {
      const pos = this.fileTableRoot.scrollTop;
      const height = this.fileTableRoot.scrollHeight;
      this.scrollPos = pos / height;
    }
  };

  public getFileSize = (node: TreeNode) => (node.type === 'file' ? node.data.fileSize : '');

  public getFileType = (node: TreeNode) => {
    if (node.type === 'file') {
      return this.saI18nPipe.transform('fileExplorer_File');
    } else if (node.type === 'folder') {
      return this.saI18nPipe.transform('fileExplorer_Folder');
    } else {
      return this.saI18nPipe.transform('fileExplorer_Other');
    }
  };

  public clickNode = (node: TreeNode) => {
    if (node.type === 'folder' || node.type === 'drive') {
      const curHtmlNode = this.fileTree.htmlNodeMap.get(node.id);
      curHtmlNode.expandNode();
      this.exploreTo(node);
      this.currentNode = node;
    } else {
      if (node.type === 'file') {
        this.download(node);
      }
    }

    this.onUpdate();
  };

  public isSelectedAnyNode = () => this.selectedTreeNodes && this.selectedTreeNodes.length > 0;

  public refreshActiveFolder = (filePath: string, uploadFolderPath: string) => {
    const matchUploadFolderPath =
      uploadFolderPath.length > 1 ? uploadFolderPath.substring(0, uploadFolderPath.length - 1) : uploadFolderPath;
    const matchFilePath = filePath.length > 1 ? filePath.substring(0, filePath.length - 1) : filePath;

    let refreshFolderPath = '';
    if (this.currentNode && this.currentNode.data) {
      const currentNodeFilePath = this.rc.deviceKind.startsWith('Windows')
        ? this.currentNode.data.fullPath.replace(/\//g, '\\')
        : this.currentNode.data.fullPath;

      if (currentNodeFilePath === matchFilePath || currentNodeFilePath === matchUploadFolderPath) {
        refreshFolderPath = currentNodeFilePath;
      }
    }

    if (refreshFolderPath.length > 0) {
      this.refreshExistingFileManagerFolder(refreshFolderPath);
    }
  };

  public refreshExistingFileManagerFolder = (folderPath: string) => {
    const refreshNode = this.findHtmlNodeByFilePath(folderPath);
    if (refreshNode) {
      this.fileSystem.getFolderContents(this.trimEnd(refreshNode.node.data.fullPath, '/') + '/*', (data: IDosFileTreeResponse) => {
        data.tree.sort(this.sortFunction);
        this.updateNodes(data.tree, refreshNode.node, this.rootNode, true);
      });
    }
  };

  public onChangeSelectedNode = (newSelectedNode: TreeNode) => {
    if (!this.selectedTreeNodes || !newSelectedNode) {
      return;
    }

    const oldSelectedNode = this.selectedTreeNodes[0];

    this.resetSelectedFiles();

    if ((!oldSelectedNode || oldSelectedNode !== newSelectedNode) && this.selectNode(newSelectedNode)) {
      this.selectedTreeNodes = [newSelectedNode];
    }

    this.onUpdate();
  };

  public sortFunction = (a: TreeNode, b: TreeNode) => {
    if (a.type < b.type) {
      return 1;
    }

    if (a.type > b.type) {
      return -1;
    }

    if (a.data.fileName.toLowerCase() > b.data.fileName.toLowerCase()) {
      return 1;
    }

    if (a.data.fileName.toLowerCase() < b.data.fileName.toLowerCase()) {
      return -1;
    }

    return 0;
  };

  public populateRootNode = (data, htmlRootNode: HtmlTreeNode) => {
    this.fileTree.populateNodes(data, this.fileTree.rootNode, htmlRootNode.childNodesTable);
    this.rootNode.appendChild(htmlRootNode.childNodes);
    this.fileTree.rootNode.nodes = data;
    this.breadCrumbNodes = [this.fileTree.rootNode];
    this.fileTree.viewRootNode = this.fileTree.rootNode;

    this.onUpdate();
  };

  public getScrollValue = (): number => this.scrollPos;

  public findTreeNode = (filePath: string): TreeNode => {
    let node: TreeNode = null;
    const htmlNode = this.findHtmlNodeByFilePath(filePath);
    if (htmlNode) {
      node = htmlNode.node;
    }
    return node;
  };

  private oldStyleSearch = (path: string, search: string) => {
    if (path === '/') {
      path = '';
    }
    if (this.searchRequestId === -1) {
      return;
    }
    this.oldSearchCount++;
    this.fileSystem.getFolderContents(path + '/*', (data: IDosFileTreeResponse) => {
      // callback returns arguments in an array
      const tree = data.tree;
      for (let i = 0; i < tree.length && this.searchRequestId !== -1; ) {
        if (tree[i].type === 'folder') {
          this.oldStyleSearch(tree[i].data.fullPath, search);
          tree.splice(i, 1);
        } else if (tree[i].data.fileName.toLowerCase().indexOf(search) !== -1) {
          i++;
        } else {
          tree.splice(i, 1);
        }
      }
      this.oldSearchCount--;
      if (this.oldSearchCount < 1) {
        data.done = true;
      }
      if (tree.length > 0 || data.done) {
        this.updateSearchResults(data);
      }
    });
  };

  public clearPreviousSearchResults = () => {
    this.searchState = FileSearchState.Ready;
    this.showFileTree();
  };

  private searchReady = () => {
    this.searchState = FileSearchState.Ready;
    this.updateSearchButtonText();
    this.searchText = '';
    this.showFileTree();
  };

  private initSearchTreeRoot = () => {
    this.searchTree.rootNode = {
      id: 0,
      title: this.saI18nPipe.transform('fileExplorer_SearchResults_Caption'),
      tooltipText: this.saI18nPipe.transform('fileExplorer_SearchResults_Caption'),
      nodes: [],
      parent: null,
      level: 0,
      data: null,
      type: 'folder',
      collapsed: true,
      isDownloading: false,
      isUploading: false,
      percentValue: 0,
    };
  };

  private initFileTreeRoot = () => {
    this.fileTree.rootNode = {
      id: 0,
      title: this.saI18nPipe.transform('fileExplorer_Root_Caption'),
      tooltipText: this.saI18nPipe.transform('fileExplorer_Root_Caption'),
      nodes: [],
      parent: null,
      level: 0,
      data: null,
      type: 'folder',
      collapsed: true,
      isDownloading: false,
      isUploading: false,
      percentValue: 0,
    };
  };

  private displayNoFilesFound = () => {
    const rowNode = document.createElement('div');
    rowNode.className = 'no-files-found-padding';
    rowNode.innerText = this.saI18nPipe.transform('fileExplorer_NoFilesFound_Caption');
    this.rootNode.appendChild(rowNode);
  };

  private trimEnd = (source: string, charlist: string) => {
    if (charlist === undefined) {
      charlist = 's';
    }

    return source.replace(new RegExp('[' + charlist + ']+$'), '');
  };

  private setFileSearchState = (fileSearchState: FileSearchState) => {
    this.searchState = fileSearchState;
    this.onUpdate();
  };

  private resetSelectedFiles = () => {
    if (this.selectedTreeNodes && this.selectedTreeNodes.length > 0) {
      this.unselectNode(this.selectedTreeNodes[0]);
      this.selectedTreeNodes = [];
    }
  };

  private onCheckFileSelection = () => {
    if (!this.selectedTreeNodes && this.selectedTreeNodes.length === 0) {
      return;
    }

    if (
      (this.searchState !== FileSearchState.Ready && !this.searchTree.isVisibleNodeInTree(this.selectedTreeNodes[0])) ||
      (this.searchState === FileSearchState.Ready && !this.fileTree.isVisibleNodeInTree(this.selectedTreeNodes[0]))
    ) {
      this.resetSelectedFiles();
      this.onUpdate();
    }
  };

  private unselectNode = (node: TreeNode) => {
    if (node) {
      const htmlNode = this.findHtmlNodeById(node.id);
      if (htmlNode) {
        htmlNode.unSelect();
      }
    }
  };

  private selectNode = (node: TreeNode): boolean => {
    if (node) {
      const htmlNode = this.findHtmlNodeById(node.id);
      if (htmlNode) {
        htmlNode.select();
        return true;
      }
    }
    return false;
  };

  //Try to find node in the active tree (file or search)
  private findNode = (node: TreeNode): TreeNode =>
    this.searchState !== FileSearchState.Ready
      ? TreeNode.findNode(this.searchTree.rootNode, node)
      : TreeNode.findNode(this.fileTree.rootNode, node);

  private findHtmlNodeById = (id: number): HtmlTreeNode =>
    this.searchState !== FileSearchState.Ready
      ? this.searchTree.htmlNodeMap.get(id)
      : this.fileTree.htmlNodeMap.get(id);

  private findHtmlNodeByFilePath = (filePath: string): HtmlTreeNode => {
    const htmlNodeMap =
      this.searchState !== FileSearchState.Ready ? this.searchTree.htmlNodeMap : this.fileTree.htmlNodeMap;

    if (!htmlNodeMap) {
      return null;
    }

    let result: HtmlTreeNode = null;
    const matchText = filePath.split('\\').join('/');
    htmlNodeMap.forEach(value => {
      if (value.node.data && value.node.data.fullPath == matchText) {
        result = value;
      }
    });
    return result;
  };

  private isWindowsDevice = (device: IDeviceModel): boolean => {
    if (
      device.family.indexOf(DEVICE_OS_FAMILY.WindowsDesktop) >= 0 ||
      device.family.indexOf(DEVICE_OS_FAMILY.WindowsModernDesktop) >= 0 ||
      device.family.indexOf(DEVICE_OS_FAMILY.WindowsPhone) >= 0
    ) {
      return true;
    } else {
      return false;
    }
  };
}
